import { getGoogleMapsApiKey, loadGoogleMaps } from "utils/googleMaps";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  N550_CENTER,
  N550_DEFAULT_ZOOM,
  SEGMENT_OPACITY,
  SEGMENT_WEIGHTS,
  SEGMENT_Z_INDEX,
} from "../models/segments";
import { getIsadColor, NO_DATA_COLOR } from "../models/isad";
import {
  CLEAN_DARK_MAP_STYLE,
  CLEAN_LIGHT_MAP_STYLE,
} from "../../liveIncidents/utils/map.utils";
import {
  WEATHER_FILL,
  WEATHER_FILL_FALLBACK,
  TRAFFIC_FLOW_COLORS,
  createBadgeIcon,
  getSegmentMidpoint,
} from "../models/conditions";
import MapControls from "./MapControls";
import ISADLegend from "./ISADLegend";
import LayerToggle from "./LayerToggle";
import { PRIORITY_COLORS } from "../../liveIncidents/models/incidents";

function getPolylineStyle(segId, isadData, selectedSegmentId) {
  const level = isadData?.[segId] ?? null;
  const color = getIsadColor(level);
  const isNoData = level === null;
  const isSelected = segId === selectedSegmentId;

  return {
    strokeColor: color,
    strokeOpacity: isNoData
      ? 0
      : isSelected
      ? SEGMENT_OPACITY.SELECTED
      : SEGMENT_OPACITY.NORMAL,
    strokeWeight: isSelected
      ? SEGMENT_WEIGHTS.SELECTED
      : SEGMENT_WEIGHTS.NORMAL,
    zIndex: isSelected ? SEGMENT_Z_INDEX.SELECTED : SEGMENT_Z_INDEX.NORMAL,
    icons: isNoData
      ? [
          {
            icon: {
              path: "M 0,-1 0,1",
              strokeOpacity: 1,
              strokeColor: NO_DATA_COLOR,
              scale: 3,
            },
            offset: "0",
            repeat: "10px",
          },
        ]
      : [],
  };
}

function getSegmentsWithinShape(event, segments) {
  const { type, overlay } = event;
  const { maps } = window.google;

  const samplePoints = (seg) => {
    const first = seg.path[0];
    const last = seg.path[seg.path.length - 1];
    const mid = {
      lat: (first.lat + last.lat) / 2,
      lng: (first.lng + last.lng) / 2,
    };
    return [first, last, mid].map((p) => new maps.LatLng(p.lat, p.lng));
  };

  return segments
    .filter((seg) => {
      const points = samplePoints(seg);
      if (type === "rectangle") {
        const bounds = overlay.getBounds();
        return points.some((pt) => bounds.contains(pt));
      }
      if (type === "polygon") {
        return points.some((pt) =>
          maps.geometry.poly.containsLocation(pt, overlay)
        );
      }
      return false;
    })
    .map((seg) => seg.id);
}

const N550BaseMap = ({
  segments = [],
  isadData = {},
  selectedSegmentId = null,
  onSegmentClick,
  onAreaSelect,
  zoomToRef = null,
  panToRef = null,
  conditionsData = { weatherZones: [], trafficFlow: {}, obuProbes: [] },
  incidents = [],
  selectedIncidentId = null,
  onIncidentClick,
}) => {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const polylinesRef = useRef(new Map());
  const drawingManagerRef = useRef(null);
  const drawnShapeRef = useRef(null);

  const weatherCirclesRef = useRef(new Map());
  const weatherInfoWindowRef = useRef(null);
  const trafficPolylinesRef = useRef(new Map());
  const obuMarkersRef = useRef(new Map());
  const obuLastSeenRef = useRef(new Map());
  const incidentMarkersRef = useRef(new Map());

  const onSegmentClickRef = useRef(onSegmentClick);
  const onAreaSelectRef = useRef(onAreaSelect);
  const segmentsRef = useRef(segments);
  const isadDataRef = useRef(isadData);
  const conditionsDataRef = useRef(conditionsData);

  const [mapReady, setMapReady] = useState(false);
  const [missingKey, setMissingKey] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isDark, setIsDark] = useState(() =>
    document.body.classList.contains("dark")
  );
  const [drawMode, setDrawMode] = useState(null);
  const [layerGroups, setLayerGroups] = useState(() => {
    try {
      const raw = sessionStorage.getItem("n550_layer_groups");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { intelligence: true, conditions: false, infra: false };
  });

  const layerGroupsRef = useRef(layerGroups);

  useEffect(() => {
    onSegmentClickRef.current = onSegmentClick;
  }, [onSegmentClick]);
  useEffect(() => {
    onAreaSelectRef.current = onAreaSelect;
  }, [onAreaSelect]);
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);
  useEffect(() => {
    isadDataRef.current = isadData;
  }, [isadData]);
  useEffect(() => {
    conditionsDataRef.current = conditionsData;
  }, [conditionsData]);
  useEffect(() => {
    layerGroupsRef.current = layerGroups;
  }, [layerGroups]);
  useEffect(() => {
    try {
      sessionStorage.setItem("n550_layer_groups", JSON.stringify(layerGroups));
    } catch {}
  }, [layerGroups]);

  useEffect(() => {
    const apiKey = getGoogleMapsApiKey();

    if (!apiKey) {
      setMissingKey(true);
      return;
    }

    let cancelled = false;
    let resizeObserver = null;
    const polylines = polylinesRef.current;

    const initMap = () => {
      if (!mapDivRef.current) return;

      // Google injects its own DOM into this container and React does not manage
      // those children, so they survive a remount or a hot reload. Constructing a
      // new Map over the previous one stacks two maps in the same box, which
      // renders as a collapsed strip. Start from an empty container.
      mapDivRef.current.replaceChildren();

      const map = new window.google.maps.Map(mapDivRef.current, {
        center: N550_CENTER,
        zoom: N550_DEFAULT_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        styles: document.body.classList.contains("dark")
          ? CLEAN_DARK_MAP_STYLE
          : CLEAN_LIGHT_MAP_STYLE,
      });
      mapRef.current = map;
      weatherInfoWindowRef.current = new window.google.maps.InfoWindow({
        disableAutoPan: true,
      });
      setMapReady(true);

      resizeObserver = new ResizeObserver(() => {
        if (!mapRef.current) return;
        window.google.maps.event.trigger(mapRef.current, "resize");
        mapRef.current.setCenter(N550_CENTER);
      });
      resizeObserver.observe(mapDivRef.current);

      // The container can settle to its final height in the same frame the map
      // is created; without this the map keeps the size it measured first.
      requestAnimationFrame(() => {
        if (!mapRef.current) return;
        window.google.maps.event.trigger(mapRef.current, "resize");
        mapRef.current.setCenter(N550_CENTER);
      });

      const dm = new window.google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        rectangleOptions: {
          fillColor: "#3B82F6",
          fillOpacity: 0.15,
          strokeColor: "#3B82F6",
          strokeWeight: 2,
          editable: false,
          zIndex: 20,
        },
        polygonOptions: {
          fillColor: "#8B5CF6",
          fillOpacity: 0.15,
          strokeColor: "#8B5CF6",
          strokeWeight: 2,
          editable: false,
          zIndex: 20,
        },
      });
      dm.setMap(map);
      drawingManagerRef.current = dm;

      dm.addListener("overlaycomplete", (event) => {
        dm.setDrawingMode(null);
        setDrawMode(null);

        if (drawnShapeRef.current) drawnShapeRef.current.setMap(null);
        drawnShapeRef.current = event.overlay;

        const matchingIds = getSegmentsWithinShape(event, segmentsRef.current);
        onAreaSelectRef.current?.(matchingIds);

        event.overlay.setMap(null);
        drawnShapeRef.current = null;
      });

      map.addListener("mousemove", (e) => {
        if (!layerGroupsRef.current.conditions || !weatherInfoWindowRef.current)
          return;
        const zones = conditionsDataRef.current.weatherZones;
        const hovered = zones.find((z) => {
          const center = new window.google.maps.LatLng(
            z.center.lat,
            z.center.lng
          );
          return (
            window.google.maps.geometry.spherical.computeDistanceBetween(
              e.latLng,
              center
            ) <= z.radiusM
          );
        });
        if (hovered) {
          const precipLine =
            hovered.precipitation_mm > 0
              ? `<br/>Precip: ${hovered.precipitation_mm} mm/h`
              : "";
          weatherInfoWindowRef.current.setContent(
            `<div style="font-family:sans-serif;font-size:11px;line-height:1.65;padding:2px 4px">` +
              `<strong>${hovered.stationId}</strong><br/>` +
              `${hovered.condition.replace(/_/g, " ")}${precipLine}<br/>` +
              `Vis: ${hovered.visibility_m} m` +
              `</div>`
          );
          weatherInfoWindowRef.current.setPosition(e.latLng);
          weatherInfoWindowRef.current.open(map);
        } else {
          weatherInfoWindowRef.current.close();
        }
      });
    };

    // Two-argument then, not .then().catch(): the rejection handler must apply
    // only to the loader. Chaining .catch() after .then() would also swallow any
    // exception thrown inside initMap, hiding a real bug behind a misleading
    // "failed to load" message.
    loadGoogleMaps(apiKey).then(
      () => {
        // The component may have unmounted while Maps was loading.
        if (!cancelled) initMap();
      },
      () => {
        if (!cancelled) setLoadFailed(true);
      }
    );

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }
      for (const pl of polylines.values()) pl.setMap(null);
      polylines.clear();
      for (const c of weatherCirclesRef.current.values()) c.setMap(null);
      weatherCirclesRef.current.clear();
      if (weatherInfoWindowRef.current) {
        weatherInfoWindowRef.current.close();
        weatherInfoWindowRef.current = null;
      }
      for (const pl of trafficPolylinesRef.current.values()) pl.setMap(null);
      trafficPolylinesRef.current.clear();
      for (const m of obuMarkersRef.current.values()) m.setMap(null);
      obuMarkersRef.current.clear();
      obuLastSeenRef.current.clear();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const update = () => setIsDark(document.body.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({
      styles: isDark ? CLEAN_DARK_MAP_STYLE : CLEAN_LIGHT_MAP_STYLE,
    });
  }, [isDark]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;

    const map = mapRef.current;
    const polylines = polylinesRef.current;

    for (const seg of segments) {
      if (!polylines.has(seg.id)) {
        const style = getPolylineStyle(
          seg.id,
          isadDataRef.current,
          selectedSegmentId
        );
        const pl = new window.google.maps.Polyline({
          path: seg.path,
          geodesic: true,
          map: layerGroupsRef.current.intelligence ? map : null,
          ...style,
        });
        pl.addListener("click", () => {
          onSegmentClickRef.current?.(seg.id);
        });
        polylines.set(seg.id, pl);
      }
    }

    const currentIds = new Set(segments.map((s) => s.id));
    for (const [id, pl] of polylines.entries()) {
      if (!currentIds.has(id)) {
        pl.setMap(null);
        polylines.delete(id);
      }
    }
  }, [segments, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!mapReady) return;
    for (const [id, pl] of polylinesRef.current.entries()) {
      pl.setOptions(getPolylineStyle(id, isadData, selectedSegmentId));
    }
  }, [isadData, selectedSegmentId, mapReady]);

  useEffect(() => {
    if (!mapReady || !zoomToRef) return;
    zoomToRef.current = (lat, lng, zoom = 14) => {
      mapRef.current?.setCenter({ lat, lng });
      mapRef.current?.setZoom(zoom);
    };
  }, [mapReady, zoomToRef]);

  useEffect(() => {
    if (!mapReady || !panToRef) return;
    panToRef.current = (lat, lng) => {
      mapRef.current?.panTo({ lat, lng });
    };
  }, [mapReady, panToRef]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const target = layerGroups.intelligence ? mapRef.current : null;
    for (const pl of polylinesRef.current.values()) {
      pl.setMap(target);
    }
  }, [layerGroups.intelligence, mapReady]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const circles = weatherCirclesRef.current;

    if (!layerGroups.conditions) {
      for (const c of circles.values()) c.setMap(null);
      weatherInfoWindowRef.current?.close();
      return;
    }

    const { weatherZones } = conditionsData;
    const nextIds = new Set(weatherZones.map((z) => z.id));

    for (const [id, c] of circles.entries()) {
      if (!nextIds.has(id)) {
        c.setMap(null);
        circles.delete(id);
      }
    }

    for (const zone of weatherZones) {
      const fill = WEATHER_FILL[zone.condition] ?? WEATHER_FILL_FALLBACK;
      if (circles.has(zone.id)) {
        circles.get(zone.id).setOptions({ ...fill, map });
      } else {
        circles.set(
          zone.id,
          new window.google.maps.Circle({
            center: zone.center,
            radius: zone.radiusM,
            map,
            clickable: false,
            zIndex: 0,
            ...fill,
          })
        );
      }
    }
  }, [conditionsData.weatherZones, layerGroups.conditions, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const polylines = trafficPolylinesRef.current;

    if (!layerGroups.conditions) {
      for (const pl of polylines.values()) pl.setMap(null);
      return;
    }

    const { trafficFlow } = conditionsData;

    for (const [id, pl] of polylines.entries()) {
      if (!trafficFlow[id]) {
        pl.setMap(null);
        polylines.delete(id);
      }
    }

    for (const seg of segments) {
      const flow = trafficFlow[seg.id];
      if (!flow) continue;
      const strokeColor =
        TRAFFIC_FLOW_COLORS[flow.status] ?? TRAFFIC_FLOW_COLORS.free_flow;
      if (polylines.has(seg.id)) {
        polylines.get(seg.id).setOptions({ strokeColor, map });
      } else {
        polylines.set(
          seg.id,
          new window.google.maps.Polyline({
            path: seg.path,
            geodesic: true,
            map,
            clickable: false,
            strokeColor,
            strokeOpacity: 0.45,
            strokeWeight: 12,
            zIndex: 0,
          })
        );
      }
    }
  }, [conditionsData.trafficFlow, segments, layerGroups.conditions, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const markers = obuMarkersRef.current;
    const lastSeen = obuLastSeenRef.current;
    const maps = window.google.maps;

    if (!layerGroups.conditions) {
      for (const m of markers.values()) m.setMap(null);
      return;
    }

    const now = Date.now();
    const FIVE_MIN = 5 * 60 * 1000;

    for (const probe of conditionsData.obuProbes) {
      lastSeen.set(probe.segment_id, probe);
    }

    for (const [segId, probe] of lastSeen.entries()) {
      if (now - probe.recorded_at > FIVE_MIN) {
        lastSeen.delete(segId);
        if (markers.has(segId)) {
          markers.get(segId).setMap(null);
          markers.delete(segId);
        }
      }
    }

    for (const [segId, probe] of lastSeen.entries()) {
      const segment = segments.find((s) => s.id === segId);
      if (!segment) continue;
      const midpoint = getSegmentMidpoint(segment.path);
      if (!midpoint) continue;
      const icon = createBadgeIcon(probe.latency_ms, maps);
      if (markers.has(segId)) {
        const m = markers.get(segId);
        m.setIcon(icon);
        m.setMap(map);
      } else {
        markers.set(
          segId,
          new maps.Marker({
            position: midpoint,
            map,
            clickable: false,
            optimized: false,
            icon,
            zIndex: 15,
          })
        );
      }
    }
  }, [conditionsData.obuProbes, segments, layerGroups.conditions, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapReady) return;
    const id = setInterval(() => {
      const now = Date.now();
      const FIVE_MIN = 5 * 60 * 1000;
      for (const [segId, probe] of obuLastSeenRef.current.entries()) {
        if (now - probe.recorded_at > FIVE_MIN) {
          obuLastSeenRef.current.delete(segId);
          const m = obuMarkersRef.current.get(segId);
          if (m) {
            m.setMap(null);
            obuMarkersRef.current.delete(segId);
          }
        }
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [mapReady]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const markers = incidentMarkersRef.current;
    const currentIds = new Set(incidents.map((inc) => inc.id));
    for (const [id, m] of markers.entries()) {
      if (!currentIds.has(id)) {
        m.setMap(null);
        markers.delete(id);
      }
    }

    for (const inc of incidents) {
      const isSelected = inc.id === selectedIncidentId;
      const icon = {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: PRIORITY_COLORS[inc.priority] ?? "#9ca3af",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
        scale: isSelected ? 10 : 7,
      };

      if (markers.has(inc.id)) {
        markers.get(inc.id).setIcon(icon);
      } else {
        const m = new window.google.maps.Marker({
          position: { lat: inc.latitude, lng: inc.longitude },
          map,
          clickable: true,
          optimized: false,
          icon,
          zIndex: 25,
        });
        m.addListener("click", () => onIncidentClick?.(inc.id));
        markers.set(inc.id, m);
      }
    }

    return () => {
      for (const m of markers.values()) m.setMap(null);
      markers.clear();
    };
  }, [incidents, selectedIncidentId, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLayerGroup = useCallback((key) => {
    setLayerGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    const dm = drawingManagerRef.current;
    if (!dm || !window.google?.maps?.drawing) return;
    const { OverlayType } = window.google.maps.drawing;
    if (drawMode === "rectangle") dm.setDrawingMode(OverlayType.RECTANGLE);
    else if (drawMode === "polygon") dm.setDrawingMode(OverlayType.POLYGON);
    else dm.setDrawingMode(null);
  }, [drawMode]);

  const handleToggleDrawRect = useCallback(() => {
    setDrawMode((prev) => (prev === "rectangle" ? null : "rectangle"));
  }, []);

  const handleToggleDrawPolygon = useCallback(() => {
    setDrawMode((prev) => (prev === "polygon" ? null : "polygon"));
  }, []);

  if (missingKey || loadFailed) {
    return (
      <div className="p-6">
        <div className="rounded border bg-yellow-50 p-4 text-sm">
          <strong>
            {missingKey
              ? "Google Maps API key not found."
              : "Google Maps failed to load."}
          </strong>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div ref={mapDivRef} className="h-full w-full" />

      <div
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <ISADLegend isDark={isDark} />
        <LayerToggle
          groups={layerGroups}
          onToggle={toggleLayerGroup}
          isDark={isDark}
        />
      </div>

      <MapControls
        onZoomIn={() =>
          mapRef.current?.setZoom(
            (mapRef.current.getZoom() ?? N550_DEFAULT_ZOOM) + 1
          )
        }
        onZoomOut={() =>
          mapRef.current?.setZoom(
            Math.max(1, (mapRef.current.getZoom() ?? N550_DEFAULT_ZOOM) - 1)
          )
        }
        onResetView={() => {
          mapRef.current?.setCenter(N550_CENTER);
          mapRef.current?.setZoom(N550_DEFAULT_ZOOM);
        }}
        onToggleDrawRect={handleToggleDrawRect}
        onToggleDrawPolygon={handleToggleDrawPolygon}
        drawMode={drawMode}
        isDark={isDark}
      />
    </div>
  );
};

export default N550BaseMap;
